import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import {
  CostComparisonEnvironment,
  EnvironmentEstimate,
  EstimateLine,
} from './types';
import {
  differenceColor,
  formatDifference,
  formatEur,
  formatEurOrDash,
} from './CostSummary';

function formatUnitPrice(line: EstimateLine): string {
  if (line.unitPrice === null) return '—';
  // Retail prices carry four decimals; rounding them to two hides the price of
  // anything cheap, like a public IP at €0.0044/hour.
  return `€${line.unitPrice.toFixed(4)}${
    line.retailUnit ? ` / ${line.retailUnit}` : ''
  }`;
}

function formatMultiplier(line: EstimateLine): string {
  if (line.monthlyMultiplier === null) return '—';
  if (line.monthlyMultiplier === 1) return '×1';
  // 730/24 is 30.416…; two decimals is enough to show where the number came from.
  return `×${Number(line.monthlyMultiplier.toFixed(2))}`;
}

const FixedLinesTable = (props: { lines: EstimateLine[] }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Resource</TableCell>
        <TableCell>SKU</TableCell>
        <TableCell>Region</TableCell>
        <TableCell align="right">Qty</TableCell>
        <TableCell align="right">Unit price</TableCell>
        <TableCell align="right">Monthly</TableCell>
        <TableCell align="right">Subtotal</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {props.lines.map(line => (
        <TableRow key={line.stableKey}>
          <TableCell>{line.displayName}</TableCell>
          <TableCell>{line.sku ?? '—'}</TableCell>
          <TableCell>{line.armRegionName}</TableCell>
          <TableCell align="right">{line.quantity}</TableCell>
          <TableCell align="right">{formatUnitPrice(line)}</TableCell>
          <TableCell align="right">{formatMultiplier(line)}</TableCell>
          <TableCell align="right">
            {line.monthlyCost === null ? '—' : formatEur(line.monthlyCost)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const UsageLinesTable = (props: { lines: EstimateLine[] }) => (
  <Box mt={2}>
    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
      Billed on usage — not included in the total
    </div>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Resource</TableCell>
          <TableCell>Region</TableCell>
          <TableCell align="right">Unit price</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {props.lines.map(line => (
          <TableRow key={line.stableKey}>
            <TableCell>
              {line.displayName}
              {line.note && (
                <div style={{ fontSize: 11, opacity: 0.7 }}>{line.note}</div>
              )}
            </TableCell>
            <TableCell>{line.armRegionName}</TableCell>
            <TableCell align="right">{formatUnitPrice(line)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const UnresolvedLines = (props: { lines: EstimateLine[] }) => (
  <Box mt={2}>
    <div style={{ fontWeight: 600, fontSize: 13, color: '#663c00' }}>
      Price unavailable — excluded from the total
    </div>
    <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 13 }}>
      {props.lines.map(line => (
        <li key={line.stableKey}>
          {line.displayName} — {line.detail ?? 'no price was resolved'}
        </li>
      ))}
    </ul>
  </Box>
);

const FreeLines = (props: { lines: EstimateLine[] }) => (
  <Accordion elevation={0}>
    <AccordionSummary>
      <span style={{ fontSize: 13 }}>
        No direct charge ({props.lines.length})
      </span>
    </AccordionSummary>
    <AccordionDetails>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
        {props.lines.map(line => (
          <li key={line.stableKey}>{line.displayName}</li>
        ))}
      </ul>
    </AccordionDetails>
  </Accordion>
);

export const EnvironmentCostCard = (props: {
  estimate: EnvironmentEstimate;
  /** Present on Day-2: what changed in this environment, and by how much. */
  comparison?: CostComparisonEnvironment;
  defaultExpanded?: boolean;
}) => {
  const { estimate, comparison } = props;
  const fixed = estimate.lines.filter(l => l.classification === 'fixed');
  const usage = estimate.lines.filter(l => l.classification === 'usage');
  const free = estimate.lines.filter(l => l.classification === 'free');
  const unresolved = estimate.lines.filter(
    l => l.classification === 'unresolved',
  );

  return (
    <Accordion defaultExpanded={props.defaultExpanded}>
      <AccordionSummary>
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          alignItems="baseline"
        >
          <span style={{ fontWeight: 600 }}>{estimate.environment}</span>
          <span>
            {formatEur(estimate.fixedMonthlyTotal)}
            {estimate.status === 'partial' && (
              <span style={{ fontSize: 12, color: '#663c00' }}> (partial)</span>
            )}
          </span>
        </Box>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        {comparison && comparison.lines.some(l => l.change !== 'unchanged') && (
          <Box mb={2}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
              What changed here
            </div>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell align="right">Before</TableCell>
                  <TableCell align="right">After</TableCell>
                  <TableCell align="right">Different</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparison.lines
                  .filter(line => line.change !== 'unchanged')
                  .map(line => (
                    <TableRow key={line.stableKey}>
                      <TableCell>{line.displayName}</TableCell>
                      <TableCell align="right">
                        {formatEurOrDash(line.before)}
                      </TableCell>
                      <TableCell align="right">
                        {formatEurOrDash(line.after)}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: differenceColor(line.different) }}
                      >
                        {formatDifference(line.different)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {fixed.length > 0 && <FixedLinesTable lines={fixed} />}
        {usage.length > 0 && <UsageLinesTable lines={usage} />}
        {unresolved.length > 0 && <UnresolvedLines lines={unresolved} />}
        {free.length > 0 && <FreeLines lines={free} />}
      </AccordionDetails>
    </Accordion>
  );
};
